import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Select, message, Alert } from "antd";
import {
  batchUpdateInvoices,
  getAllPartners,
  getAccountTree,
  clearAccountingCache,
  clearAccountsCache,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import { useSelector } from "react-redux";
import {
  invalidateCacheByPrefix,
} from "../../../context/AccountingDataCache";

const { Option } = Select;

/** Same rendering rules as InvoiceItemColumns (kept local — do not import from shared columns file). */
function renderAccountTreeForBatch(accounts) {
  if (!accounts || !accounts.length) {
    return null;
  }
  const buildIndentedOptions = (items, depth = 0, includeSelf = true) => {
    return items.flatMap((account) => {
      const hasChildren =
        Array.isArray(account.children) && account.children.length > 0;
      const indent = depth > 0 ? `${"\u2014 ".repeat(depth)}` : "";
      const nodes = [];
      const selectable = !!(account.is_postable || !hasChildren);
      const label = account.code
        ? `${account.code} - ${account.name}`
        : account.name;
      if (includeSelf) {
        nodes.push(
          <Select.Option
            key={account.id}
            value={account.id}
            disabled={!selectable}
          >
            {`${indent}${label}`}
          </Select.Option>
        );
      }
      if (hasChildren) {
        nodes.push(...buildIndentedOptions(account.children, depth + 1, true));
      }
      return nodes;
    });
  };
  return accounts.map((root) => (
    <Select.OptGroup key={root.id} label={`${root.code || ""} - ${root.name}`}>
      {buildIndentedOptions([root], 0, false)}
    </Select.OptGroup>
  ));
}

const BatchEditModal = ({
  visible,
  onCancel,
  onSuccess,
  selectedIds = [],
  invoiceType = "bill",
  currentBrand,
}) => {
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [partners, setPartners] = useState([]);
  /** Same API as CreateInvoiceModal: getAccountTree({}). */
  const [accounts, setAccounts] = useState([]);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetchBrandsBySelectedClients(selectedClients);
      setBrands(response.data || []);
    } catch {
      message.error("Failed to fetch brands");
    }
  }, [selectedClients]);

  const fetchPartnersList = useCallback(async () => {
    try {
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      const response = await getAllPartners(filters);
      let list = response.data || [];
      if (invoiceType === "bill") {
        list = list.filter(
          (p) => p.partner_type === "vendor" || p.partner_type === "both"
        );
      } else {
        list = list.filter(
          (p) => p.partner_type === "customer" || p.partner_type === "both"
        );
      }
      setPartners(list);
    } catch {
      message.error("Failed to fetch partners");
    }
  }, [currentBrand, invoiceType]);

  const fetchAccountsList = useCallback(async () => {
    try {
      const response = await getAccountTree({});
      let accountsData = [];
      if (Array.isArray(response)) {
        accountsData = response;
      } else if (response && Array.isArray(response.data)) {
        accountsData = response.data;
      } else if (response && Array.isArray(response.results)) {
        accountsData = response.results;
      } else if (response && typeof response === "object") {
        accountsData = response.data || response.results || [];
      }
      if (accountsData && accountsData.length > 0) {
        setAccounts(accountsData);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      message.error(
        `Failed to fetch chart of accounts: ${
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message ||
          "Unknown error"
        }`
      );
      setAccounts([]);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      fetchBrands();
      fetchPartnersList();
      fetchAccountsList();
    }
  }, [visible, fetchBrands, fetchPartnersList, fetchAccountsList, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const updates = {};
      if (values.brand) updates.brand = values.brand;
      if (values.partner) updates.partner = values.partner;
      if (values.account) updates.account = values.account;

      if (Object.keys(updates).length === 0) {
        message.warning("Please select at least one field to update");
        return;
      }

      setLoading(true);
      const result = await batchUpdateInvoices(selectedIds, updates);
      const hadAccountUpdate = Boolean(values.account);

      clearAccountingCache();
      clearAccountsCache();
      invalidateCacheByPrefix("chart-of-accounts");
      invalidateCacheByPrefix("account_tree_");
      invalidateCacheByPrefix("account_balances_");
      window.dispatchEvent(new CustomEvent("accountsUpdated"));

      if (result.total_skipped > 0) {
        const skippedNums = (result.skipped || [])
          .map((s) => s.number || s.id)
          .join(", ");
        const coaNote = hadAccountUpdate
          ? " Chart of account was still updated on bills where you selected an account."
          : "";
        message.warning(
          `Updated ${result.total_updated} item(s). Partner/brand not changed on ${result.total_skipped} (allocations — unallocate first): ${skippedNums}.${coaNote}`,
          10
        );
      } else if (result.total_errors > 0) {
        message.warning(
          `Updated ${result.total_updated} item(s), ${result.total_errors} failed`
        );
      } else {
        message.success(`Successfully updated ${result.total_updated} item(s)`);
      }

      onSuccess?.();
      onCancel();
    } catch (error) {
      const detail =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message;
      message.error(detail || "Batch update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Batch Edit ${selectedIds.length} ${invoiceType === "bill" ? "Bill" : "Invoice"}(s)`}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText="Apply Changes"
      destroyOnClose
      zIndex={1100}
    >
      <Alert
        type="info"
        message="Leave a field empty to keep its current value. If a bill has payment allocations, partner and brand changes are skipped until you unallocate; chart of account line-item changes are allowed and re-post to the ledger for reporting."
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical">
        <Form.Item name="brand" label="Brand">
          <Select
            placeholder="Select new brand (optional)"
            allowClear
            showSearch
            getPopupContainer={(n) => n?.closest?.(".ant-modal-wrap") || document.body}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {brands.map((b) => (
              <Option key={b.id} value={b.id}>
                {b.brand_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="partner" label="Partner">
          <Select
            placeholder="Select new partner (optional)"
            allowClear
            showSearch
            getPopupContainer={(n) => n?.closest?.(".ant-modal-wrap") || document.body}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {partners.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="account" label="Chart of Account (applies to all line items)">
          <Select
            placeholder="Select new account (optional)"
            allowClear
            showSearch
            optionFilterProp="children"
            listHeight={400}
            getPopupContainer={(n) => n?.closest?.(".ant-modal-wrap") || document.body}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 450 }}
          >
            {renderAccountTreeForBatch(accounts)}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BatchEditModal;
